/**
 * Sayz Find My Size — Host Stock Badge (editorial, luxury-grade).
 *
 * Communicates the merchant's live stock status for the recommended size
 * on the Result + Welcome Recommend screens WITHOUT resorting to the
 * generic green-tick / red-cross iconography that bargain checkout widgets
 * use. The design philosophy here is:
 *
 *   • Typography IS the signal. Uppercase tracking-wider caption, serif-
 *     weight accent character, a hairline rule under or through the size.
 *     No filled icons.
 *   • The badge NEVER sits on top of the size letter — it anchors beneath
 *     it so the 72 px size stays the hero.
 *   • "In stock" is quiet confidence (muted uppercase caption + hairline
 *     accent). "Out of stock" is honest, not alarmist (dimmed letter,
 *     diagonal hairline strike, calm advisory text).
 *   • The badge is OPTIONAL. If the merchant hasn't configured
 *     outOfStockSelector OR the host DOM selector doesn't match anything,
 *     we return null and the recommendation screen reads clean — no
 *     "stock unknown" placeholder (would read as a bug to the visitor).
 */

import { Check } from "lucide-react";

/**
 * Sayz Find My Size — Host Stock Badge (editorial, luxury-grade).
 *
 * Communicates the merchant's live stock status for the recommended size
 * on the Result + Welcome Recommend screens WITHOUT resorting to the
 * generic green-tick / red-cross iconography that bargain checkout widgets
 * use. The design philosophy here is:
 *
 *   • Typography IS the signal. Uppercase tracking-wider caption with a
 *     slim 1.75 stroke check icon — the ritual of the tape loader icons.
 *   • The badge NEVER sits on top of the size letter — it anchors beneath
 *     it so the 72 px size stays the hero.
 *   • "In stock" uses a matte sage green (#5B8A6A) — editorial, premium,
 *     NOT the neon success-green of discount-app checkout flows.
 *   • "Out of stock" is honest, not alarmist (dimmed letter, diagonal
 *     hairline strike, calm advisory text).
 *   • The badge is OPTIONAL. If the merchant hasn't configured
 *     outOfStockSelector OR the host DOM selector doesn't match anything,
 *     we return null and the recommendation screen reads clean — no
 *     "stock unknown" placeholder (would read as a bug to the visitor).
 */

// Matte sage — hand-picked for the editorial aesthetic: HSL(142, 20%, 45%)
// equivalent. Reads as "stokta, güvenle alabilirsin" without the saturation
// hit of a system-success green. Pairs cleanly with every accent color
// (orange/burgundy/graphite) merchants pick in the panel.
const SAGE = "#5B8A6A";

export const HostStockBadge = ({ status, accent }) => {
    void accent; // reserved — in-stock no longer uses accent; keeps call-sites unchanged
    if (status === "in-stock") {
        return (
            <div
                className="inline-flex items-center gap-1.5"
                data-testid="fms-host-stock-in-stock"
            >
                {/* Check icon — slim 1.75 stroke mirrors the tape loader
                    icon weight, so "STOKTA" reads as part of the same
                    editorial language as the measuring tape and ÖLÇÜYOR
                    eyebrow (not a shouty success tick). */}
                <Check
                    size={13}
                    strokeWidth={1.75}
                    aria-hidden="true"
                    style={{ color: SAGE, flexShrink: 0 }}
                />
                <span
                    className="text-[10px] font-semibold uppercase leading-none"
                    style={{
                        color: SAGE,
                        letterSpacing: "0.22em",
                    }}
                >
                    Stokta
                </span>
            </div>
        );
    }

    if (status === "out-of-stock") {
        return (
            <div
                className="flex flex-col items-center space-y-1.5"
                data-testid="fms-host-stock-out-of-stock"
            >
                {/* Hairline rule — 32 px wide, 1 px, neutralized to muted
                    foreground so it reads as a struck line rather than an
                    accent flourish. Positioned under the dimmed size letter
                    (see ResultStep render path — the parent dims the size
                    letter opacity to .55 when status === "out-of-stock"). */}
                <span
                    aria-hidden="true"
                    style={{
                        display: "block",
                        width: 32,
                        height: 1,
                        background: "currentColor",
                        opacity: 0.35,
                        borderRadius: 1,
                    }}
                />
                <span
                    className="text-[10px] font-semibold uppercase leading-none text-muted-foreground"
                    style={{
                        letterSpacing: "0.22em",
                    }}
                >
                    Şu an stokta yok
                </span>
            </div>
        );
    }

    // "disabled" / "not-found" / anything else → no badge. Keeping the
    // recommendation screen clean when we honestly can't tell.
    return null;
};

export default HostStockBadge;
